/** Real client for "สคริปต์แนะนำรายคน" (same origin + session cookie) — see ./types.ts for the routes. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type { ConversationScript, OrderRead, RepeatOrder, ScriptApi } from './types'

/** "เติมจากแชททั้งหมด": one model call + the shipping calculator (the API gives up at 40 s). */
const READ_TIMEOUT_MS = 60000

export class HttpScriptApi implements ScriptApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async req<T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown, timeoutMs = this.timeoutMs): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
      const res = await this.fetchImpl(new URL(`${this.base.replace(/\/+$/, '')}/api/v1${path}`, window.location.origin).toString(), {
        method,
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let message = res.status === 403 ? 'ไม่มีสิทธิ์ทำรายการนี้' : `HTTP ${res.status}`
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') message = j.detail
        } catch {
          /* not JSON */
        }
        throw new ApiError(res.status, message)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'ระบบไม่ตอบในเวลาที่กำหนด ลองใหม่อีกครั้ง')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง')
    } finally {
      clearTimeout(timer)
    }
  }

  private async orNull<T>(p: Promise<T>): Promise<T | null> {
    try {
      return await p
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null
      throw e
    }
  }

  get(convId: string): Promise<ConversationScript | null> {
    return this.orNull(this.req('GET', `/conversations/${encodeURIComponent(convId)}/script`))
  }

  refresh(convId: string): Promise<ConversationScript | null> {
    return this.orNull(this.req('POST', `/conversations/${encodeURIComponent(convId)}/script/refresh`))
  }

  repeatOrder(convId: string): Promise<RepeatOrder | null> {
    return this.orNull(this.req('GET', `/conversations/${encodeURIComponent(convId)}/script/repeat-order`))
  }

  stepDone(convId: string, key: string, done: boolean): Promise<ConversationScript> {
    const path = `/conversations/${encodeURIComponent(convId)}/script/steps/${encodeURIComponent(key)}/done`
    return this.req(done ? 'POST' : 'DELETE', path) // 404 / 422 carry the reason: the panel says it
  }

  orderRead(convId: string, opts: { auto?: boolean } = {}): Promise<OrderRead> {
    const path = `/conversations/${encodeURIComponent(convId)}/order-read`
    return this.req('POST', path, { auto: !!opts.auto }, READ_TIMEOUT_MS) // 403 / 409 / 422 / 5xx carry the reason
  }
}
